import { useEffect, useState } from 'react';
import fetchAPI from '../helper/axios';
import fetchAPICustom from '../helper/custom';
import { CareerPageData } from '../types/career';

const cache = new Map<string, { data: unknown; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000;
const MAX_RETRIES = 3;

const fetchWithCache = async <T>(
  key: string,
  fn: () => Promise<T>
): Promise<T> => {
  const now = Date.now();
  const cached = cache.get(key);

  if (cached && now - cached.timestamp < CACHE_DURATION) {
    return cached.data as T;
  }

  const data = await fn();
  cache.set(key, { data, timestamp: now });
  return data;
};

export const useCareerData = () => {
  const [data, setData] = useState<CareerPageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState<number>(0);

  useEffect(() => {
    let retryTimeout: ReturnType<typeof setTimeout>;

    const load = async () => {
      try {
        if (retry === 0) setLoading(true);

        const [
          about,
          heroImg,
          heroImgmob,
          about2,
          about3,
          heroImg2,
          slider,
        ] = await Promise.all([
          fetchWithCache('career-node-33', () => fetchAPI('node/33')),
          fetchWithCache('career-media-38', () => fetchAPI('media/38/edit')),
          fetchWithCache('career-media-174', () => fetchAPI('media/174/edit')),
          fetchWithCache('career-node-34', () => fetchAPI('node/34')),
          fetchWithCache('career-node-35', () => fetchAPI('node/35')),
          fetchWithCache('career-media-39', () => fetchAPI('media/39/edit')),
          fetchWithCache('career-slider-5', () =>
            fetchAPICustom('webslider.php?id=5')
          ),
        ]);
   const sliderImages =
        slider?.data?.slider_images?.map((s: any) => ({
          ...s,
          slider_img_url: s.slider_img_url?.replace('public://', '') || '',
        })) || [];

        setData({
          about,
          heroImg,
          heroImgmob,
          about2,
          about3,
          heroImg2,
         slider: { ...slider, data: { ...slider.data, slider_images: sliderImages } },
        });

        setError(null);
      } catch {
        if (retry < MAX_RETRIES) {
          retryTimeout = setTimeout(() => setRetry(r => r + 1), 3000);
        } else {
          setError('Failed to load career data');
        }
      } finally {
        if (retry === 0) setLoading(false);
      }
    };

    load();
    return () => retryTimeout && clearTimeout(retryTimeout);
  }, [retry]);

  return { data, loading, error };
};
